// src/components/labels/LabelTemplateEditor.jsx
import React, { useMemo, useState, useRef, useEffect } from "react";
import { Rnd } from "react-rnd";
import JsBarcode from "jsbarcode";
import QRCode from "react-qr-code";
import { mmToPx, pxToMm } from "../../utils/units";
import { useGetAllBranchesQuery } from "../../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../../toolkitServices/categoryApi";

/* ---------------- Fonts ---------------- */
const BASE_FONT_FAMILY =
  '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const MONO_FONT_FAMILY =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

/* ---------------- Field mapping ---------------- */
const AVAILABLE_FIELDS = [
  { label: "Item Code", value: "itemCode" },
  { label: "Purity", value: "purityName" },
  { label: "Category", value: "categoryName" },
  { label: "Product", value: "productName" },
  { label: "Design", value: "designName" },
  { label: "Branch", value: "branchName" },
  { label: "Counter", value: "counterName" },
  { label: "Vendor", value: "vendorName" },
  { label: "Gross Wt", value: "grossWt" },
  { label: "Net Wt", value: "netWt" },
  { label: "Stone Wt", value: "stoneWt" },
  { label: "Diamond Wt", value: "diamondWt" },
  { label: "Qty", value: "qty" },
  { label: "Pcs", value: "pcs" },
  { label: "MRP", value: "mrp" },
  { label: "Sample Name", value: "sampleName" },
  { label: "HUID Code", value: "huidCode" },
  { label: "Lot No", value: "lotNo" },
];

const NUMERIC_FIELDS = [
  "grossWt",
  "netWt",
  "stoneWt",
  "diamondWt",
  "qty",
  "pcs",
  "mrp",
];

/* ---------------- Dummy preview item ---------------- */
const PREVIEW_ITEM = {
  itemCode: "MW-CH-0001",
  purityName: "22K",
  categoryName: "Chain",
  productName: "Plain Chain",
  designName: "DC-101",
  branchName: "Main Br.",
  counterName: "Counter 1",
  vendorName: "ABC Jewellers",
  grossWt: 12.45,
  netWt: 12.12,
  stoneWt: 0.33,
  diamondWt: 0,
  qty: 1,
  pcs: 1,
  mrp: 123456,
  sampleName: "Classic Chain",
  huidCode: "HUID123456",
  lotNo: "LOT-001",
};

/* ---------------- Keyboard nudge (mm) ---------------- */
const NUDGE_SMALL_MM = 0.1;
const NUDGE_BIG_MM = 0.5;

/* ---------------- Barcode preview (SVG via JsBarcode) ---------------- */
const BarcodePreview = ({ value, heightPx, selected }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !value) return;
    try {
      JsBarcode(ref.current, value, {
        format: "CODE128",
        displayValue: false,
        height: Math.max(8, heightPx),
        margin: 0,
      });
    } catch (e) {
      console.error("Barcode error:", e);
    }
  }, [value, heightPx]);

  return (
    <svg
      ref={ref}
      shapeRendering="crispEdges"
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: "#ffffff",
        borderRadius: 2,
        outline: selected
          ? "1px solid rgba(79,70,229,0.5)"
          : "1px solid transparent",
      }}
    />
  );
};

/* ---------------- Default template ---------------- */
const PROFESSIONAL_DEFAULT_TEMPLATE = {
  name: "Default 50x15 (QR + Barcode)",
  widthMm: 50,
  heightMm: 15,
  dpi: 96,

  branchIds: [],
  categoryIds: [],
  appliesToAllBranches: false,
  appliesToAllCategories: false,

  elements: [
    {
      id: "brand_name",
      type: "text",
      contentType: "static",
      staticText: "Your Brand Name",
      fieldKey: "",
      x: 2,
      y: 0.6,
      w: 46,
      h: 3,
      fontSize: 8.5,
      bold: true,
      textAlign: "center",
      prefix: "",
    },
    {
      id: "qr_itemcode",
      type: "qrcode",
      contentType: "field",
      fieldKey: "itemCode",
      x: 1,
      y: 3.2,
      w: 8.5,
      h: 8.5,
      prefix: "",
    },
    {
      id: "itm_code",
      type: "text",
      contentType: "field",
      fieldKey: "itemCode",
      staticText: "",
      x: 10.2,
      y: 3.2,
      w: 38,
      h: 3,
      fontSize: 9,
      bold: true,
      textAlign: "left",
      prefix: PREVIEW_ITEM.itemCode,
    },
    {
      id: "purity",
      type: "text",
      contentType: "field",
      fieldKey: "purityName",
      staticText: "",
      x: 10.2,
      y: 6.1,
      w: 11,
      h: 3,
      fontSize: 7.5,
      bold: true,
      textAlign: "left",
      prefix: PREVIEW_ITEM.purityName,
    },
    {
      id: "gwt",
      type: "text",
      contentType: "field",
      fieldKey: "grossWt",
      staticText: "",
      x: 22,
      y: 6.1,
      w: 12,
      h: 3,
      fontSize: 7.5,
      bold: false,
      textAlign: "left",
      prefix: PREVIEW_ITEM.grossWt.toString(),
    },
    {
      id: "nwt",
      type: "text",
      contentType: "field",
      fieldKey: "netWt",
      staticText: "",
      x: 34.5,
      y: 6.1,
      w: 12,
      h: 3,
      fontSize: 7.5,
      bold: false,
      textAlign: "left",
      prefix: PREVIEW_ITEM.netWt.toString(),
    },
    {
      id: "mrp",
      type: "text",
      contentType: "field",
      fieldKey: "mrp",
      staticText: "",
      x: 10.2,
      y: 9.1,
      w: 24,
      h: 3.4,
      fontSize: 8,
      bold: true,
      textAlign: "left",
      prefix: PREVIEW_ITEM.mrp.toString(),
    },
    {
      id: "cat_prod",
      type: "text",
      contentType: "field",
      fieldKey: "categoryName",
      staticText: "",
      x: 35,
      y: 9.1,
      w: 14,
      h: 3.4,
      fontSize: 7,
      bold: false,
      textAlign: "right",
      prefix: PREVIEW_ITEM.categoryName,
    },
    {
      id: "barcode_itemcode",
      type: "barcode",
      contentType: "field",
      fieldKey: "itemCode",
      x: 2,
      y: 11.2,
      w: 46,
      h: 3.2,
      barcodeFormat: "CODE128",
      prefix: "",
    },
  ],
};

/* ---------------- Helpers ---------------- */
const normalizeList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.rows)) return res.rows;
  if (Array.isArray(res.results)) return res.results;
  return [];
};

const LabelTemplateEditor = ({ initialTemplate, onSave, isSaving = false }) => {
  const [template, setTemplate] = useState(() => {
    const base =
      initialTemplate && initialTemplate.elements?.length
        ? initialTemplate
        : PROFESSIONAL_DEFAULT_TEMPLATE;

    return {
      ...PROFESSIONAL_DEFAULT_TEMPLATE,
      ...base,
      branchIds: base.branchIds || [],
      categoryIds: base.categoryIds || [],
      appliesToAllBranches: base.appliesToAllBranches || false,
      appliesToAllCategories: base.appliesToAllCategories || false,
    };
  });

  const [selectedElementId, setSelectedElementId] = useState(null);

  // Mobile bottom panel tab
  const [panelTab, setPanelTab] = useState("tools"); // tools | props | template

  // Zoom
  const [zoom, setZoom] = useState(2);

  // Dropdown open states (desktop + mobile template tab)
  const [branchOpen, setBranchOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  // edit mode initialTemplate sync
  useEffect(() => {
    if (initialTemplate && initialTemplate.elements?.length) {
      setTemplate((prev) => ({
        ...prev,
        ...initialTemplate,
        branchIds: initialTemplate.branchIds || prev.branchIds || [],
        categoryIds: initialTemplate.categoryIds || prev.categoryIds || [],
        appliesToAllBranches:
          initialTemplate.appliesToAllBranches ??
          prev.appliesToAllBranches ??
          false,
        appliesToAllCategories:
          initialTemplate.appliesToAllCategories ??
          prev.appliesToAllCategories ??
          false,
      }));
    }
  }, [initialTemplate]);

  const selectedElement = useMemo(
    () => template.elements.find((e) => e.id === selectedElementId) || null,
    [template.elements, selectedElementId]
  );

  const updateTemplate = (patch) =>
    setTemplate((prev) => ({ ...prev, ...patch }));

  const updateElement = (id, patch) => {
    setTemplate((prev) => ({
      ...prev,
      elements: prev.elements.map((el) =>
        el.id === id ? { ...el, ...patch } : el
      ),
    }));
  };

  const deleteElement = (id) => {
    setTemplate((prev) => ({
      ...prev,
      elements: prev.elements.filter((el) => el.id !== id),
    }));
    if (selectedElementId === id) setSelectedElementId(null);
  };

  const addTextElement = (contentType) => {
    const id = `el_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const defaultFieldKey = contentType === "field" ? "itemCode" : "";
    const defaultSample =
      contentType === "field" && PREVIEW_ITEM[defaultFieldKey]
        ? String(PREVIEW_ITEM[defaultFieldKey])
        : "";

    const newEl = {
      id,
      type: "text",
      contentType,
      fieldKey: defaultFieldKey,
      staticText: contentType === "static" ? "Sample Text" : "",
      x: 2,
      y: 2,
      w: 30,
      h: 4,
      fontSize: 9,
      bold: false,
      textAlign: "left",
      prefix: contentType === "field" ? defaultSample : "",
    };

    setTemplate((prev) => ({ ...prev, elements: [...prev.elements, newEl] }));
    setSelectedElementId(id);
    setPanelTab("props");
  };

  const addBarcodeElement = () => {
    const id = `el_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const newEl = {
      id,
      type: "barcode",
      contentType: "field",
      fieldKey: "itemCode",
      x: 2,
      y: 8,
      w: 40,
      h: 6,
      barcodeFormat: "CODE128",
    };
    setTemplate((prev) => ({ ...prev, elements: [...prev.elements, newEl] }));
    setSelectedElementId(id);
    setPanelTab("props");
  };

  const addQrElement = () => {
    const id = `el_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const newEl = {
      id,
      type: "qrcode",
      contentType: "field",
      fieldKey: "itemCode",
      x: 5,
      y: 5,
      w: 10,
      h: 10,
      prefix: "",
    };
    setTemplate((prev) => ({ ...prev, elements: [...prev.elements, newEl] }));
    setSelectedElementId(id);
    setPanelTab("props");
  };

  const handleSave = () => {
    if (onSave) onSave(template);
    else {
      alert("Template JSON console me log ho gaya (backend integration baad me).");
    }
  };

  /* ---------------- Keyboard nudge ---------------- */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        !selectedElementId ||
        !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
      ) {
        return;
      }

      const target = e.target;
      const tag = target.tagName;
      const isTyping =
        tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
      if (isTyping) return;

      e.preventDefault();
      const step = e.shiftKey ? NUDGE_BIG_MM : NUDGE_SMALL_MM;

      setTemplate((prev) => {
        const widthMm = prev.widthMm || 0;
        const heightMm = prev.heightMm || 0;

        const elements = prev.elements.map((el) => {
          if (el.id !== selectedElementId) return el;

          let { x = 0, y = 0, w = 0, h = 0 } = el;

          switch (e.key) {
            case "ArrowUp":
              y -= step;
              break;
            case "ArrowDown":
              y += step;
              break;
            case "ArrowLeft":
              x -= step;
              break;
            case "ArrowRight":
              x += step;
              break;
            default:
              break;
          }

          const maxX = Math.max(0, widthMm - w);
          const maxY = Math.max(0, heightMm - h);

          const clampedX = Math.min(Math.max(0, x), maxX);
          const clampedY = Math.min(Math.max(0, y), maxY);

          return {
            ...el,
            x: parseFloat(clampedX.toFixed(3)),
            y: parseFloat(clampedY.toFixed(3)),
          };
        });

        return { ...prev, elements };
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedElementId]);

  /* ---------------- Branch / Category data ---------------- */
  const {
    data: branchRes,
    isLoading: branchesLoading,
    isError: branchesError,
  } = useGetAllBranchesQuery({ page: 1, limit: 1000, active: true });

  const {
    data: categoryRes,
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useGetAllCategoriesQuery({ page: 1, limit: 1000, active: true });

  const branches = useMemo(() => normalizeList(branchRes), [branchRes]);
  const categories = useMemo(() => normalizeList(categoryRes), [categoryRes]);

  const getId = (item) => item?._id || item?.id || item?.value;
  const getBranchLabel = (b) =>
    b?.name || b?.branchName || b?.code || b?.label || "Unnamed";
  const getCategoryLabel = (c) =>
    c?.name || c?.categoryName || c?.label || "Unnamed";

  const toggleBranch = (id) => {
    setTemplate((prev) => {
      if (prev.appliesToAllBranches) return prev;
      const existing = prev.branchIds || [];
      const next = existing.includes(id)
        ? existing.filter((x) => x !== id)
        : [...existing, id];
      return { ...prev, branchIds: next };
    });
  };

  const toggleCategory = (id) => {
    setTemplate((prev) => {
      if (prev.appliesToAllCategories) return prev;
      const existing = prev.categoryIds || [];
      const next = existing.includes(id)
        ? existing.filter((x) => x !== id)
        : [...existing, id];
      return { ...prev, categoryIds: next };
    });
  };

  const selectAllBranches = () => {
    const allIds = branches.map((b) => getId(b)).filter(Boolean);
    setTemplate((prev) => ({
      ...prev,
      appliesToAllBranches: false,
      branchIds: allIds,
    }));
  };

  const clearBranches = () =>
    setTemplate((prev) => ({
      ...prev,
      appliesToAllBranches: false,
      branchIds: [],
    }));

  const selectAllCategories = () => {
    const allIds = categories.map((c) => getId(c)).filter(Boolean);
    setTemplate((prev) => ({
      ...prev,
      appliesToAllCategories: false,
      categoryIds: allIds,
    }));
  };

  const clearCategories = () =>
    setTemplate((prev) => ({
      ...prev,
      appliesToAllCategories: false,
      categoryIds: [],
    }));

  const hasBranchSelection = template.appliesToAllBranches
    ? true
    : (template.branchIds || []).length > 0;

  /* ---------------- px sizes (zoom only UI) ---------------- */
  const baseWidthPx = mmToPx(template.widthMm, template.dpi);
  const baseHeightPx = mmToPx(template.heightMm, template.dpi);
  const baseSnapPx = mmToPx(0.5, template.dpi);

  const labelWidthPx = baseWidthPx * zoom;
  const labelHeightPx = baseHeightPx * zoom;
  const snapSizePx = baseSnapPx * zoom;

  /* ---------------- RENDER BLOCKS (IMPORTANT FIX: render funcs, not inner components) ---------------- */
  const renderTemplateSettings = () => (
    <div className="space-y-3">
      <div>
        <label className="block text-[11px] font-semibold text-slate-700 mb-1">
          Template Name
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-slate-300 rounded-xl text-[12px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
          value={template.name}
          onChange={(e) => updateTemplate({ name: e.target.value })}
          placeholder="e.g. Default 50x15"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] font-semibold text-slate-700 mb-1">
            Width (mm)
          </label>
          <input
            type="number"
            min={10}
            className="w-full px-3 py-2 border border-slate-300 rounded-xl text-[12px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
            value={template.widthMm}
            onChange={(e) =>
              updateTemplate({ widthMm: Number(e.target.value) || 0 })
            }
          />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-700 mb-1">
            Height (mm)
          </label>
          <input
            type="number"
            min={5}
            className="w-full px-3 py-2 border border-slate-300 rounded-xl text-[12px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
            value={template.heightMm}
            onChange={(e) =>
              updateTemplate({ heightMm: Number(e.target.value) || 0 })
            }
          />
        </div>
      </div>

      {/* Branch & Category */}
      <div className="space-y-3">
        {/* Branch */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] font-semibold text-slate-700">
              Branches
            </label>
            <label className="flex items-center gap-1 text-[10px] text-slate-500">
              <input
                type="checkbox"
                className="accent-[#9481ce]"
                checked={template.appliesToAllBranches}
                onChange={(e) =>
                  updateTemplate({
                    appliesToAllBranches: e.target.checked,
                    branchIds: e.target.checked ? [] : template.branchIds,
                  })
                }
              />
              <span>All</span>
            </label>
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setBranchOpen((p) => !p)}
              disabled={template.appliesToAllBranches}
              className={`w-full flex items-center justify-between px-3 py-2 border rounded-xl text-[11px] bg-white focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 ${
                template.appliesToAllBranches
                  ? "border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed"
                  : "border-slate-300 hover:bg-slate-50 focus:border-[#9481ce]"
              }`}
            >
              <span className="truncate text-slate-700">
                {branchesLoading
                  ? "Loading branches..."
                  : template.appliesToAllBranches
                  ? "All branches"
                  : template.branchIds?.length
                  ? `${template.branchIds.length} branch selected`
                  : "Select branch(es)"}
              </span>
              <span className="ml-2 text-[9px] text-slate-500">
                {branchOpen ? "▲" : "▼"}
              </span>
            </button>

            {branchOpen && !template.appliesToAllBranches && (
              <div className="absolute z-40 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-xl max-h-56 overflow-y-auto">
                {branchesError && (
                  <div className="px-3 py-2 text-[11px] text-red-500">
                    Error loading branches
                  </div>
                )}
                {!branchesLoading && !branchesError && branches.length === 0 && (
                  <div className="px-3 py-2 text-[11px] text-slate-500">
                    No branches found
                  </div>
                )}

                {!branchesLoading && branches.length > 0 && (
                  <>
                    <div className="flex justify-between px-3 py-2 border-b border-slate-100 bg-slate-50 text-[10px]">
                      <button
                        type="button"
                        onClick={selectAllBranches}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={clearBranches}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        Clear
                      </button>
                    </div>

                    {branches.map((b) => {
                      const id = getId(b);
                      if (!id) return null;
                      const checked = (template.branchIds || []).includes(id);
                      return (
                        <label
                          key={id}
                          className="flex items-center gap-2 px-3 py-2 text-[11px] text-slate-700 cursor-pointer hover:bg-slate-50"
                        >
                          <input
                            type="checkbox"
                            className="accent-[#9481ce]"
                            checked={checked}
                            onChange={() => toggleBranch(id)}
                          />
                          <span className="truncate">{getBranchLabel(b)}</span>
                        </label>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Category */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] font-semibold text-slate-700">
              Categories
            </label>
            <label className="flex items-center gap-1 text-[10px] text-slate-500">
              <input
                type="checkbox"
                className="accent-[#9481ce]"
                checked={template.appliesToAllCategories}
                onChange={(e) =>
                  updateTemplate({
                    appliesToAllCategories: e.target.checked,
                    categoryIds: e.target.checked ? [] : template.categoryIds,
                  })
                }
              />
              <span>All</span>
            </label>
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => {
                if (!hasBranchSelection) return;
                setCategoryOpen((p) => !p);
              }}
              disabled={!hasBranchSelection || template.appliesToAllCategories}
              className={`w-full flex items-center justify-between px-3 py-2 border rounded-xl text-[11px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 ${
                !hasBranchSelection || template.appliesToAllCategories
                  ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                  : "border-slate-300 bg-white hover:bg-slate-50 focus:border-[#9481ce]"
              }`}
            >
              <span className="truncate">
                {!hasBranchSelection
                  ? "Select branch first"
                  : template.appliesToAllCategories
                  ? "All categories"
                  : categoriesLoading
                  ? "Loading categories..."
                  : template.categoryIds?.length
                  ? `${template.categoryIds.length} category selected`
                  : "Select category(es)"}
              </span>
              <span className="ml-2 text-[9px] text-slate-500">
                {categoryOpen ? "▲" : "▼"}
              </span>
            </button>

            {categoryOpen &&
              hasBranchSelection &&
              !template.appliesToAllCategories && (
                <div className="absolute z-40 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-xl max-h-56 overflow-y-auto">
                  {categoriesError && (
                    <div className="px-3 py-2 text-[11px] text-red-500">
                      Error loading categories
                    </div>
                  )}
                  {!categoriesLoading &&
                    !categoriesError &&
                    categories.length === 0 && (
                      <div className="px-3 py-2 text-[11px] text-slate-500">
                        No categories found
                      </div>
                    )}

                  {!categoriesLoading && categories.length > 0 && (
                    <>
                      <div className="flex justify-between px-3 py-2 border-b border-slate-100 bg-slate-50 text-[10px]">
                        <button
                          type="button"
                          onClick={selectAllCategories}
                          className="text-slate-600 hover:text-slate-900"
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          onClick={clearCategories}
                          className="text-slate-600 hover:text-slate-900"
                        >
                          Clear
                        </button>
                      </div>

                      {categories.map((c) => {
                        const id = getId(c);
                        if (!id) return null;
                        const checked = (template.categoryIds || []).includes(id);
                        return (
                          <label
                            key={id}
                            className="flex items-center gap-2 px-3 py-2 text-[11px] text-slate-700 cursor-pointer hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              className="accent-[#9481ce]"
                              checked={checked}
                              onChange={() => toggleCategory(id)}
                            />
                            <span className="truncate">
                              {getCategoryLabel(c)}
                            </span>
                          </label>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
          </div>

          <p className="mt-1 text-[10px] text-slate-500">
            Selected branches ke andar selected categories pe template apply hoga.
          </p>
        </div>
      </div>
    </div>
  );

  const renderTools = (compact = false) => (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => addTextElement("field")}
          className="w-full text-[12px] bg-indigo-600 text-white rounded-xl py-2 hover:bg-indigo-700 font-semibold"
        >
          + Text
        </button>
        <button
          onClick={() => addTextElement("static")}
          className="w-full text-[12px] bg-slate-900 text-white rounded-xl py-2 hover:bg-black font-semibold"
        >
          + Static
        </button>
        <button
          onClick={addBarcodeElement}
          className="w-full text-[12px] bg-emerald-600 text-white rounded-xl py-2 hover:bg-emerald-700 font-semibold"
        >
          + Barcode
        </button>
        <button
          onClick={addQrElement}
          className="w-full text-[12px] bg-blue-600 text-white rounded-xl py-2 hover:bg-blue-700 font-semibold"
        >
          + QR
        </button>
      </div>

      {!compact && (
        <div className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3">
          <div className="font-semibold text-slate-700 mb-1">Design Tips</div>
          <ul className="space-y-1 list-disc list-inside">
            <li>QR size 8–10mm se kam mat rakho.</li>
            <li>Black text on white = best thermal clarity.</li>
            <li>Weights/amount ke liye mono font best.</li>
          </ul>
        </div>
      )}
    </div>
  );

  const renderProperties = () => {
    if (!selectedElement) {
      return (
        <div className="text-[11px] text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-xl px-3 py-3">
          Canvas me kisi element pe click karo — properties yaha dikhengi.
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[12px] font-semibold text-slate-800">
            {selectedElement.type === "text"
              ? "Text"
              : selectedElement.type === "barcode"
              ? "Barcode"
              : "QR Code"}
          </div>
          <button
            onClick={() => deleteElement(selectedElement.id)}
            className="text-[11px] px-3 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
          >
            Delete
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 text-[11px]">
          {[
            { k: "x", label: "X (mm)" },
            { k: "y", label: "Y (mm)" },
            { k: "w", label: "W (mm)" },
            { k: "h", label: "H (mm)" },
          ].map((f) => (
            <div key={f.k}>
              <label className="block text-slate-500 mb-1">{f.label}</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-slate-300 rounded-xl text-[12px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
                value={selectedElement[f.k]}
                onChange={(e) =>
                  updateElement(selectedElement.id, {
                    [f.k]: Number(e.target.value) || 0,
                  })
                }
              />
            </div>
          ))}
        </div>

        {selectedElement.type === "text" && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">
                  Font Size
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-[12px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
                  value={selectedElement.fontSize}
                  onChange={(e) =>
                    updateElement(selectedElement.id, {
                      fontSize: Number(e.target.value) || 8,
                    })
                  }
                />
              </div>

              <label className="flex items-center gap-2 mt-6 text-[12px] text-slate-700">
                <input
                  type="checkbox"
                  className="accent-[#9481ce]"
                  checked={selectedElement.bold || false}
                  onChange={(e) =>
                    updateElement(selectedElement.id, { bold: e.target.checked })
                  }
                />
                Bold
              </label>
            </div>

            <div>
              <label className="block text-[11px] text-slate-500 mb-1">
                Text Align
              </label>
              <div className="grid grid-cols-3 gap-2">
                {["left", "center", "right"].map((align) => (
                  <button
                    key={align}
                    onClick={() =>
                      updateElement(selectedElement.id, { textAlign: align })
                    }
                    className={`py-2 rounded-xl border text-[12px] font-semibold ${
                      selectedElement.textAlign === align
                        ? "bg-[#9481ce] text-white border-[#9481ce]"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {align}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[11px] text-slate-500 mb-1">
                Content Type
              </label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-xl text-[12px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
                value={selectedElement.contentType}
                onChange={(e) =>
                  updateElement(selectedElement.id, {
                    contentType: e.target.value,
                  })
                }
              >
                <option value="field">Field</option>
                <option value="static">Static</option>
              </select>
            </div>

            {selectedElement.contentType === "field" && (
              <>
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">
                    Field
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-[12px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
                    value={selectedElement.fieldKey}
                    onChange={(e) => {
                      const newKey = e.target.value;
                      const prevKey = selectedElement.fieldKey;
                      const prevSample = PREVIEW_ITEM[prevKey];
                      const prevSampleStr =
                        prevSample != null ? String(prevSample) : "";

                      const shouldReplace =
                        !selectedElement.prefix ||
                        selectedElement.prefix === prevSampleStr ||
                        selectedElement.prefix === "[Field]";

                      const nextSample = PREVIEW_ITEM[newKey];
                      updateElement(selectedElement.id, {
                        fieldKey: newKey,
                        prefix:
                          shouldReplace && nextSample != null
                            ? String(nextSample)
                            : selectedElement.prefix,
                      });
                    }}
                  >
                    {AVAILABLE_FIELDS.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">
                    Preview Value
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-[12px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
                    value={selectedElement.prefix || ""}
                    onChange={(e) =>
                      updateElement(selectedElement.id, {
                        prefix: e.target.value,
                      })
                    }
                    placeholder="e.g. MW-CH-0001"
                  />
                </div>
              </>
            )}

            {selectedElement.contentType === "static" && (
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">
                  Static Text
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-[12px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
                  value={selectedElement.staticText || ""}
                  onChange={(e) =>
                    updateElement(selectedElement.id, {
                      staticText: e.target.value,
                    })
                  }
                />
              </div>
            )}
          </>
        )}

        {["barcode", "qrcode"].includes(selectedElement.type) && (
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">
              Field (value encoded)
            </label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-xl text-[12px] focus:outline-none focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]"
              value={selectedElement.fieldKey}
              onChange={(e) =>
                updateElement(selectedElement.id, { fieldKey: e.target.value })
              }
            >
              {AVAILABLE_FIELDS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>

            <p className="mt-1 text-[10px] text-slate-500">
              Print time pe actual value encode hoga.
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="px-3 sm:px-4 md:px-6 pb-4">
      {/* Full-height workspace (avoid page fat) */}
      <div className="mx-auto max-w-7xl h-[calc(100vh-120px)] md:h-[calc(100vh-90px)] min-h-[560px] flex flex-col gap-3">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight truncate">
              Label Template Designer
            </h1>
            <p className="text-[11px] sm:text-xs text-slate-500 mt-1">
              MM-based WYSIWYG • QR + Barcode • Thermal-friendly output
            </p>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="hidden sm:block text-[11px] text-slate-500 text-right">
              Size:{" "}
              <span className="font-semibold text-slate-800">
                {template.widthMm}mm × {template.heightMm}mm
              </span>
            </div>

            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`inline-flex items-center justify-center px-4 py-2 rounded-xl font-semibold text-[12px] text-white shadow-sm transition ${
                isSaving
                  ? "bg-[#9481ce]/60 cursor-wait"
                  : "bg-[#9481ce] hover:bg-[#7d6ec0]"
              }`}
            >
              {isSaving ? "Saving..." : "Save Template"}
            </button>
          </div>
        </div>

        {/* MAIN EDITOR (responsive + scroll fixed) */}
        <div className="bg-white rounded-2xl shadow-md border border-slate-200 flex-1 min-h-0 overflow-hidden">
          <div className="h-full min-h-0 overflow-hidden flex flex-col lg:grid lg:grid-cols-[300px_minmax(0,1fr)_340px]">
            {/* DESKTOP LEFT */}
            <aside className="hidden lg:flex min-h-0 flex-col border-r border-slate-200 bg-white">
              <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3">
                <div className="text-[12px] font-semibold text-slate-800">
                  Template & Tools
                </div>
                <div className="text-[10px] text-slate-500">
                  Apply rules + add elements
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-4">
                {renderTemplateSettings()}
                {renderTools(false)}
              </div>
            </aside>

            {/* CANVAS (always visible) */}
            <main className="min-h-0 overflow-hidden flex flex-col bg-white">
              {/* top bar */}
              <div className="border-b border-slate-200 bg-slate-50 px-3 sm:px-4 py-2 flex items-center justify-between gap-2">
                <div className="text-[10px] sm:text-[11px] text-slate-600 truncate">
                  Drag & resize (0.5mm snap) • Arrow 0.1mm • Shift+Arrow 0.5mm
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] text-slate-500">
                    Zoom {Math.round(zoom * 100)}%
                  </span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.25}
                    value={zoom}
                    onChange={(e) =>
                      setZoom(Math.max(1, Number(e.target.value) || 1))
                    }
                    className="w-24 sm:w-28"
                  />
                </div>
              </div>

              {/* canvas scroll area */}
              <div className="flex-1 min-h-0 overflow-auto overscroll-contain bg-slate-50 p-3 sm:p-4">
                <div className="w-full h-full flex items-center justify-center">
                  <div
                    className="relative overflow-hidden rounded-2xl"
                    style={{
                      width: labelWidthPx,
                      height: labelHeightPx,
                      fontFamily: BASE_FONT_FAMILY,
                      WebkitFontSmoothing: "antialiased",
                      MozOsxFontSmoothing: "grayscale",
                      backgroundColor: "#ffffff",
                      boxShadow: "0 8px 22px rgba(15,23,42,0.18)",
                    }}
                  >
                    {/* subtle grid */}
                    <div
                      className="absolute inset-0 pointer-events-none opacity-30"
                      style={{
                        backgroundImage:
                          "radial-gradient(circle, #e5e7eb 0.55px, transparent 0.55px)",
                        backgroundSize: "4px 4px",
                      }}
                    />

                    <div className="absolute inset-0">
                      {template.elements.map((el) => {
                        const elWidthPx = mmToPx(el.w, template.dpi) * zoom;
                        const elHeightPx = mmToPx(el.h, template.dpi) * zoom;
                        const elXpx = mmToPx(el.x, template.dpi) * zoom;
                        const elYpx = mmToPx(el.y, template.dpi) * zoom;

                        const isSelected = el.id === selectedElementId;
                        const isNumericField =
                          el.type === "text" &&
                          el.contentType === "field" &&
                          NUMERIC_FIELDS.includes(el.fieldKey);

                        const handleSelect = () => {
                          setSelectedElementId(el.id);
                          // mobile: bring properties quickly
                          setPanelTab("props");
                        };

                        return (
                          <Rnd
                            key={el.id}
                            size={{ width: elWidthPx, height: elHeightPx }}
                            position={{ x: elXpx, y: elYpx }}
                            bounds="parent"
                            dragGrid={[snapSizePx, snapSizePx]}
                            resizeGrid={[snapSizePx, snapSizePx]}
                            onMouseDown={handleSelect}
                            onDragStart={handleSelect}
                            onResizeStart={handleSelect}
                            onDragStop={(e, d) => {
                              const newXmm = pxToMm(d.x / zoom, template.dpi);
                              const newYmm = pxToMm(d.y / zoom, template.dpi);
                              updateElement(el.id, {
                                x: parseFloat(newXmm.toFixed(2)),
                                y: parseFloat(newYmm.toFixed(2)),
                              });
                            }}
                            onResizeStop={(e, direction, ref, delta, position) => {
                              const newWmm = pxToMm(
                                ref.offsetWidth / zoom,
                                template.dpi
                              );
                              const newHmm = pxToMm(
                                ref.offsetHeight / zoom,
                                template.dpi
                              );
                              const newXmm = pxToMm(
                                position.x / zoom,
                                template.dpi
                              );
                              const newYmm = pxToMm(
                                position.y / zoom,
                                template.dpi
                              );

                              updateElement(el.id, {
                                x: parseFloat(newXmm.toFixed(2)),
                                y: parseFloat(newYmm.toFixed(2)),
                                w: parseFloat(newWmm.toFixed(2)),
                                h: parseFloat(newHmm.toFixed(2)),
                              });
                            }}
                            className="rounded-[2px]"
                            style={{
                              border: "none",
                              outline: isSelected
                                ? "1px solid rgba(79,70,229,0.95)"
                                : "1px dashed rgba(148,163,184,0.95)",
                              backgroundColor: isSelected
                                ? "rgba(238,242,255,0.65)"
                                : "transparent",
                              zIndex: isSelected ? 20 : 1,
                              userSelect: "none",
                            }}
                          >
                            {/* TEXT */}
                            {el.type === "text" &&
                              (() => {
                                if (el.contentType === "static") {
                                  const value = el.staticText || "[Static]";
                                  const isPlaceholder = !el.staticText;
                                  return (
                                    <div
                                      className="w-full h-full"
                                      style={{
                                        fontSize: el.fontSize,
                                        fontWeight: el.bold ? 600 : 400,
                                        textAlign: el.textAlign || "left",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent:
                                          el.textAlign === "center"
                                            ? "center"
                                            : el.textAlign === "right"
                                            ? "flex-end"
                                            : "flex-start",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        lineHeight: 1,
                                        fontFamily: BASE_FONT_FAMILY,
                                        color: "#111827",
                                        margin: 0,
                                        padding: 0,
                                      }}
                                    >
                                      <span
                                        className={`truncate ${
                                          isPlaceholder
                                            ? "text-slate-400 italic"
                                            : "text-slate-900"
                                        }`}
                                        style={{ fontSize: `${el.fontSize || 9}px` }}
                                      >
                                        {value}
                                      </span>
                                    </div>
                                  );
                                }

                                const hasCustom =
                                  el.prefix && el.prefix.trim() !== "";
                                const display = hasCustom ? el.prefix : "[Field]";
                                const isPlaceholder = !hasCustom;

                                return (
                                  <div
                                    className="w-full h-full"
                                    style={{
                                      fontSize: el.fontSize,
                                      fontWeight: el.bold ? 600 : 400,
                                      textAlign: el.textAlign || "left",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent:
                                        el.textAlign === "center"
                                          ? "center"
                                          : el.textAlign === "right"
                                          ? "flex-end"
                                          : "flex-start",
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                      lineHeight: 1,
                                      fontFamily: isNumericField
                                        ? MONO_FONT_FAMILY
                                        : BASE_FONT_FAMILY,
                                      color: "#111827",
                                      margin: 0,
                                      padding: 0,
                                    }}
                                  >
                                    <span
                                      className={`truncate ${
                                        isPlaceholder
                                          ? "text-slate-400 italic"
                                          : "text-slate-900"
                                      }`}
                                      style={{ fontSize: `${el.fontSize || 9}px` }}
                                    >
                                      {display}
                                    </span>
                                  </div>
                                );
                              })()}

                            {/* BARCODE */}
                            {el.type === "barcode" && (
                              <BarcodePreview
                                value={String(PREVIEW_ITEM[el.fieldKey] || "")}
                                heightPx={Math.max(8, elHeightPx - 2)}
                                selected={isSelected}
                              />
                            )}

                            {/* QR */}
                            {el.type === "qrcode" && (
                              <div className="w-full h-full flex items-center justify-center">
                                <QRCode
                                  value={String(PREVIEW_ITEM[el.fieldKey] || "")}
                                  style={{ width: "100%", height: "100%" }}
                                  level="M"
                                  bgColor="#ffffff"
                                  fgColor="#000000"
                                />
                              </div>
                            )}
                          </Rnd>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile quick add bar */}
              <div className="lg:hidden border-t border-slate-200 bg-white px-3 py-2 flex items-center gap-2 overflow-x-auto">
                <button
                  onClick={() => addTextElement("field")}
                  className="px-3 py-1.5 rounded-full bg-indigo-600 text-white text-[11px] font-semibold whitespace-nowrap"
                >
                  + Text
                </button>
                <button
                  onClick={addBarcodeElement}
                  className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-semibold whitespace-nowrap"
                >
                  + Barcode
                </button>
                <button
                  onClick={addQrElement}
                  className="px-3 py-1.5 rounded-full bg-blue-600 text-white text-[11px] font-semibold whitespace-nowrap"
                >
                  + QR
                </button>

                <button
                  onClick={() => setPanelTab("template")}
                  className="ml-auto px-3 py-1.5 rounded-full border border-slate-200 text-[11px] font-semibold text-slate-700 bg-white whitespace-nowrap"
                >
                  Template
                </button>
              </div>
            </main>

            {/* DESKTOP RIGHT */}
            <aside className="hidden lg:flex min-h-0 flex-col border-l border-slate-200 bg-white">
              <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3">
                <div className="text-[12px] font-semibold text-slate-800">
                  Properties
                </div>
                <div className="text-[10px] text-slate-500">
                  {selectedElement
                    ? `Selected: ${selectedElement.type}`
                    : "No selection"}
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4">
                {renderProperties()}
              </div>
            </aside>

            {/* MOBILE/TABLET BOTTOM PANEL (fixed height + inner scroll ✅) */}
            <section className="lg:hidden border-t border-slate-200 bg-white h-[46vh] max-h-[420px] min-h-[280px] min-h-0 overflow-hidden flex flex-col">
              <div className="px-2 py-2 border-b bg-slate-50 flex items-center gap-2">
                {[
                  { key: "tools", label: "Tools" },
                  { key: "props", label: "Properties" },
                  { key: "template", label: "Template" },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setPanelTab(t.key)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition ${
                      panelTab === t.key
                        ? "bg-[#6b59c6] text-white border-[#6b59c6]"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}

                <div className="ml-auto text-[10px] text-slate-500 truncate">
                  {selectedElement
                    ? `Selected: ${selectedElement.type}`
                    : "No selection"}
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3">
                {panelTab === "tools" && renderTools(true)}
                {panelTab === "props" && renderProperties()}
                {panelTab === "template" && (
                  <div className="space-y-4">
                    {renderTemplateSettings()}
                    {renderTools(true)}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabelTemplateEditor;
